import * as React from "react";
import { Button, Text, makeStyles, shorthands, tokens } from "@fluentui/react-components";
import { trackException } from "../services/app-insights.service";

const useStyles = makeStyles({
    root: {
        // Fills the task pane so the fallback is visible without scrolling.
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        ...shorthands.padding(tokens.spacingVerticalXL),
        gap: tokens.spacingVerticalM,
        textAlign: "center",
    },
    title: {
        fontWeight: 600,
    },
    detail: {
        color: tokens.colorNeutralForeground3,
        maxWidth: "32rem",
    },
});

interface ErrorBoundaryProps {
    children: React.ReactNode;
}

interface ErrorBoundaryState {
    error: Error | null;
}

/**
 * Top-level React error boundary. Catches render-time exceptions thrown by
 * the component tree (which window.onerror doesn't see — React swallows
 * them) and routes them to App Insights via `trackException`. Renders a
 * minimal "something went wrong" fallback with a reload button so the user
 * can recover without restarting Excel.
 *
 * Intentionally a single app-wide boundary rather than per-page: page-local
 * boundaries hide the failure from telemetry as "partial degradation," but
 * what we actually want to know is whether anyone hit it at all.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    state: ErrorBoundaryState = { error: null };

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        // The component stack is the most useful debugging signal — it points
        // at the React tree path that threw, which window.onerror can't see.
        // App Insights' "exceptions" table will get the stack via error.stack.
        trackException(error, {
            source: "react_error_boundary",
            componentStack: errorInfo.componentStack ?? "",
        });
        console.error("ErrorBoundary caught:", error, errorInfo);
    }

    private handleReload = (): void => {
        window.location.reload();
    };

    render() {
        if (!this.state.error) return this.props.children;
        return <ErrorBoundaryFallback error={this.state.error} onReload={this.handleReload} />;
    }
}

/**
 * Fallback UI. Separate functional component so it can use Fluent UI's
 * `makeStyles` hook (class components can't).
 */
const ErrorBoundaryFallback: React.FC<{ error: Error; onReload: () => void }> = ({ error, onReload }) => {
    const styles = useStyles();
    return (
        <div className={styles.root}>
            <Text size={500} className={styles.title}>
                Something went wrong
            </Text>
            <Text className={styles.detail}>
                The add-in hit an unexpected error and can't continue from here. Reloading
                will get you back to a working state. The error has been logged for the team.
            </Text>
            <Text size={200} className={styles.detail}>
                {error.message}
            </Text>
            <Button appearance="primary" onClick={onReload}>
                Reload
            </Button>
        </div>
    );
};
